import { AlertTriangle, XCircle } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import useTranslations from "@/shared/lang/useTranslations";

import { CardStatementToolbar } from "../components/statement/CardStatementToolbar";
import { StatementFooter } from "../components/statement/StatementFooter";
import { StatementMasthead } from "../components/statement/StatementMasthead";
import { StatementMovementsTable } from "../components/statement/StatementMovementsTable";
import { StatementPage } from "../components/statement/StatementPage";
import { StatementPartiesBlock } from "../components/statement/StatementPartiesBlock";
import { StatementSummaryStrip } from "../components/statement/StatementSummaryStrip";

import "../components/statement/statementPrint.css";
import { useCardStatement } from "../hooks/useCardStatement";
import { useStatementPagination } from "../hooks/useStatementPagination";
import { StatementPeriodMode } from "../types/card-statement.types";

const localeFor = (lang: string) => (lang === "es" ? "es-ES" : lang === "pt" ? "pt-BR" : "en-US");

const currentMonthKey = () => {
  const now = new Date();
  return `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, "0")}`;
};

const todayIso = () => new Date().toISOString().slice(0, 10);

const thirtyDaysAgoIso = () => {
  const date = new Date();
  date.setUTCDate(date.getUTCDate() - 30);
  return date.toISOString().slice(0, 10);
};

const formatDay = (raw: string, lang: string) => {
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return raw;
  return date.toLocaleDateString(localeFor(lang), { day: "2-digit", month: "short", year: "numeric" });
};

export const CardStatementPage = () => {
  const { id: cardId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t, activeLanguage } = useTranslations("cards");

  const [mode, setMode] = useState<StatementPeriodMode>(StatementPeriodMode.MONTH);
  const [month, setMonth] = useState(currentMonthKey);
  const [range, setRange] = useState({ from: thirtyDaysAgoIso(), to: todayIso() });

  const { statement, isLoading, error, refetch } = useCardStatement({
    cardId,
    mode,
    month,
    from: range.from,
    to: range.to,
  });

  const { pages, pageCount } = useStatementPagination(statement?.transactions ?? []);

  const periodLabel = useMemo(() => {
    if (!statement) return "";
    if (statement.period.mode === StatementPeriodMode.LIFETIME) return t("statement_period_lifetime");
    return t("statement_period_range", {
      from: formatDay(statement.period.from, activeLanguage),
      to: formatDay(statement.period.to, activeLanguage),
    });
  }, [statement, t, activeLanguage]);

  const issuedAtLabel = statement ? formatDay(statement.issued_at, activeLanguage) : "";

  // Chrome uses the document title as the default PDF filename.
  useEffect(() => {
    if (!statement) return;
    const previous = document.title;
    document.title = `Extracto-${statement.card.last_4}-${statement.statement_number}`;
    return () => {
      document.title = previous;
    };
  }, [statement]);

  const handleDownload = useCallback(() => {
    // Awaiting the fonts avoids the first print of a session paginating against fallback-font metrics.
    void document.fonts.ready.then(() => window.print());
  }, []);

  const isEstimated = !!statement && !statement.reconciliation.reconciled;

  return (
    <div className="statement-root min-h-svh bg-muted">
      <CardStatementToolbar
        mode={mode}
        month={month}
        from={range.from}
        to={range.to}
        cardCreatedAt={statement?.card.created_at}
        isLoading={isLoading}
        canPrint={!!statement}
        onModeChange={setMode}
        onMonthChange={setMonth}
        onRangeChange={setRange}
        onBack={() => navigate(`/app/cards/cards/${cardId ?? ""}`)}
        onDownload={handleDownload}
      />

      {/*
        The paper is a fixed 210mm, wider than a phone. `w-fit` + `mx-auto` centres it when it fits and
        anchors it left when it doesn't, so panning reaches both edges — flex centring would leave the left
        side unreachable once the content overflows.
      */}
      <div className="statement-scroll overflow-x-auto px-4 py-8">
        <div className="statement-stack mx-auto flex w-fit flex-col gap-8">
          {error && !statement && (
            <div className="statement-screen-only w-full max-w-[900px]">
              <ColocaAlert
                variant="destructive"
                icon={XCircle}
                heading={t("statement_error_load")}
                text={error}
                actions={
                  <ColocaButton variant="default" size="sm" onClick={refetch}>
                    {t("statement_error_retry")}
                  </ColocaButton>
                }
              />
            </div>
          )}

          {!statement && !error && (
            <div
              className="statement-screen-only statement-page shrink-0 animate-pulse rounded-sm bg-card/60 shadow-lg motion-reduce:animate-none"
              aria-label={t("statement_loading")}
            />
          )}

          {statement?.truncated && (
            <div className="statement-screen-only w-full max-w-[900px]">
              <ColocaAlert
                variant="warning"
                icon={AlertTriangle}
                heading={t("statement_notice_truncated_title")}
                text={t("statement_notice_truncated_description", {
                  total: statement.transaction_count,
                  shown: statement.returned_count,
                })}
              />
            </div>
          )}

          {statement &&
            pages.map((pageTransactions, pageIndex) => {
              const isFirst = pageIndex === 0;
              const isLast = pageIndex === pageCount - 1;

              return (
                <StatementPage key={pageIndex} pageIndex={pageIndex}>
                  <StatementMasthead
                    variant={isFirst ? "full" : "compact"}
                    card={statement.card}
                    statementNumber={statement.statement_number}
                    periodLabel={periodLabel}
                    issuedAtLabel={issuedAtLabel}
                  />

                  {isFirst && (
                    <>
                      <StatementPartiesBlock holder={statement.holder} account={statement.account} />
                      <StatementSummaryStrip totals={statement.totals} isEstimated={isEstimated} />
                    </>
                  )}

                  <StatementMovementsTable
                    transactions={pageTransactions}
                    showHeading={isFirst}
                    netTotal={isLast ? statement.totals.net : null}
                    currency={statement.totals.currency}
                  />

                  <StatementFooter
                    issuedAtLabel={issuedAtLabel}
                    pageIndex={pageIndex}
                    pageCount={pageCount}
                    showNotes={isLast}
                    hasDeclined={statement.totals.declined_count > 0}
                    isEstimated={isEstimated}
                    holderFromCardFallback={statement.holder.source === "CARD_FALLBACK"}
                    truncated={statement.truncated}
                    shownCount={statement.returned_count}
                    totalCount={statement.transaction_count}
                  />
                </StatementPage>
              );
            })}
        </div>
      </div>
    </div>
  );
};
